import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ChevronRightIcon, 
  ChevronLeftIcon,
  LocationMarkerIcon, 
  EyeIcon, 
  CheckCircleIcon, 
  ClockIcon,
  DesktopComputerIcon,
  TagIcon,
  ChartBarIcon,
  UserGroupIcon,
  ArrowRightIcon,
  CheckIcon,
  SparklesIcon,
  PlusCircleIcon,
  LightningBoltIcon,
  CurrencyDollarIcon,
  ShoppingBagIcon,
  OfficeBuildingIcon,
  HeartIcon,
  AcademicCapIcon
} from '@heroicons/react/outline';

// Données de démonstration pour les packages basés sur les types de sites
const packageData = [
  {
    id: 1,
    name: "Retail Network",
    subtitle: "Supermarché & Centres commerciaux",
    description: "Un réseau premium couvrant les principaux centres commerciaux et supermarchés de la ville, touchant une clientèle diversifiée pendant ses moments d'achat.",
    price: 350000,
    duration: "7 jours",
    siteCount: 12,
    screenCount: 24,
    estimatedViews: 85000,
    featured: false,
    color: "#E84E0E",
    lightColor: "#FFF4F0",
    locations: ["Douala Centre", "Akwa", "Bonanjo"],
    siteTypes: ["Supermarchés", "Centres commerciaux", "Boutiques premium"],
    benefits: [
      "Présence dans 12 sites commerciaux stratégiques",
      "24 écrans LCD HD en intérieur",
      "Audience clientèle premium en situation d'achat",
      "Ciblage durant les heures d'affluence",
      "Visibilité optimale près des caisses et entrées",
      "Rapport de performance détaillé"
    ],
    targetAudience: "Familles urbaines, Cadres, Jeunes actifs, Décideurs d'achat",
    idealFor: "Lancements de produits, Promotions saisonnières, Offres spéciales, Nouveaux services",
    analytics: true,
    designSupport: "Standard",
    targetingSectors: ["Alimentaire", "Mode", "Électronique", "Cosmétiques", "Services"],
    popularity: 88,
    icon: "shopping",
    imageUrl: "/assets/site_carrefour1.jpg",
    imageSites: [
      "/assets/site_carrefour1.jpg",
      "/assets/site_carrefour2.jpg",
      "/assets/site_carrefour3.jpg"
    ]
  },
  {
    id: 2,
    name: "Business District",
    subtitle: "Quartiers d'affaires & Entreprises",
    description: "Ciblez les professionnels et décideurs dans leurs environnements de travail avec notre réseau d'écrans présents dans les halls d'immeubles de bureaux et zones d'affaires.",
    price: 450000,
    duration: "14 jours",
    siteCount: 15,
    screenCount: 30,
    estimatedViews: 70000,
    featured: true,
    color: "#8B5CF6",
    lightColor: "#F5F3FF",
    locations: ["Bonanjo", "Bonapriso", "Akwa"],
    siteTypes: ["Tours de bureaux", "Sièges sociaux", "Centres d'affaires"],
    benefits: [
      "15 sites premium dans les quartiers d'affaires",
      "30 écrans stratégiquement placés dans les lobbies et ascenseurs",
      "Visibilité auprès des cadres et décideurs",
      "Diffusion en heures de bureau (7h-19h)",
      "Ciblage sectoriel possible par zones d'activité",
      "Analyse démographique des impressions"
    ],
    targetAudience: "Cadres, Dirigeants, Entrepreneurs, Professionnels",
    idealFor: "Services B2B, Solutions professionnelles, Services financiers, Formation, Événements corporate",
    analytics: true,
    designSupport: "Premium",
    targetingSectors: ["Finance", "Conseil", "Technologies", "Formation", "Services aux entreprises"],
    popularity: 92,
    icon: "building",
    imageUrl: "/assets/site_4.jpg",
    imageSites: [
      "/assets/site_5.jpg",
      "/assets/site_6.jpg",
      "/assets/site_7.jpg"
    ]
  },
  {
    id: 3,
    name: "Lifestyle & Leisure",
    subtitle: "Restaurants, Cafés & Lieux de détente",
    description: "Touchez une clientèle détendue et réceptive dans les restaurants, cafés et espaces de loisirs premium de la ville avec une présence visuelle élégante.",
    price: 280000,
    duration: "10 jours",
    siteCount: 18,
    screenCount: 25,
    estimatedViews: 60000,
    featured: false,
    color: "#10B981",
    lightColor: "#ECFDF5",
    locations: ["Akwa", "Bonapriso", "Bali", "Deido"],
    siteTypes: ["Restaurants haut de gamme", "Cafés branchés", "Bars lounge", "Espaces détente"],
    benefits: [
      "18 établissements premium sélectionnés",
      "25 écrans discrets intégrés aux décors",
      "Audience captive et détendue",
      "Programmation intelligente selon les heures de repas",
      "Association à des environnements agréables",
      "Format créatif adapté à l'ambiance des lieux"
    ],
    targetAudience: "Urban lifestyle, Cadres, Couples, Groupes d'amis, Entrepreneurs",
    idealFor: "Marques lifestyle, Boissons, Événements culturels, Applications, Mode, Voyages",
    analytics: true,
    designSupport: "Créatif",
    targetingSectors: ["Food & Beverage", "Culture", "Mode", "Tourisme", "Tech lifestyle"],
    popularity: 85,
    icon: "heart",
    imageUrl: "/assets/site_8.jpg",
    imageSites: [
      "/assets/site_8.jpg",
      "/assets/site_9.jpg",
      "/assets/site_10.jpg"
    ]
  },
  {
    id: 4,
    name: "Knowledge Network",
    subtitle: "Universités, Campus & Centres de formation",
    description: "Connectez-vous avec la jeunesse étudiante et les apprenants dans les établissements d'enseignement et centres de formation professionnelle.",
    price: 320000,
    duration: "14 jours",
    siteCount: 10,
    screenCount: 22,
    estimatedViews: 45000,
    featured: false,
    color: "#F59E0B",
    lightColor: "#FEF3C7",
    locations: ["Campus universitaires", "Écoles supérieures", "Centres de formation"],
    siteTypes: ["Universités", "Écoles d'ingénieurs", "Business schools", "Centres de certification"],
    benefits: [
      "10 établissements éducatifs prestigieux",
      "22 écrans dans les halls, cafétérias et bibliothèques",
      "Audience jeune, connectée et en formation",
      "Diffusion adaptée au calendrier académique",
      "Affinité naturelle avec l'innovation et la technologie",
      "Environnement propice à l'attention"
    ],
    targetAudience: "Étudiants, Jeunes diplômés, Enseignants, Chercheurs, Professionnels en formation",
    idealFor: "Technologies, Applications, Stages, Premier emploi, Services aux étudiants, Formations",
    analytics: true,
    designSupport: "Standard + Student Edition",
    targetingSectors: ["Éducation", "Technologie", "Édition", "Recrutement", "Services numériques"],
    popularity: 78,
    icon: "academic",
    imageUrl: "/assets/site_11.jpg",
    imageSites: [
      "/assets/site_12.jpg",
      "/assets/site_13.jpg",
      "/assets/site_14.jpg"
    ]
  }
];

const PackagesDisplay = () => {
  const [currentPackage, setCurrentPackage] = useState(null);
  const [carouselIndex, setCarouselIndex] = useState(0);
  const [isCompareMode, setIsCompareMode] = useState(false);
  const [selectedPackages, setSelectedPackages] = useState([]);
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 0);

  // Nombre de packages à afficher par slide selon la largeur d'écran
  const getItemsPerSlide = () => {
    if (windowWidth >= 1280) return 3;
    if (windowWidth >= 768) return 2;
    return 1;
  };

  const itemsPerSlide = getItemsPerSlide();
  const maxIndex = Math.max(0, packageData.length - itemsPerSlide);

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const openPackageDetails = (pkg) => {
    setCurrentPackage(pkg);
  };

  const closePackageDetails = () => {
    setCurrentPackage(null);
  };

  const toggleCompareMode = () => {
    setIsCompareMode(!isCompareMode);
    setSelectedPackages([]);
  };

  const togglePackageSelection = (pkg) => {
    if (selectedPackages.some(item => item.id === pkg.id)) {
      setSelectedPackages(selectedPackages.filter(item => item.id !== pkg.id));
    } else {
      if (selectedPackages.length < 3) {
        setSelectedPackages([...selectedPackages, pkg]);
      }
    }
  };

  const previousSlide = () => {
    setCarouselIndex(Math.max(0, carouselIndex - 1));
  };

  const nextSlide = () => {
    setCarouselIndex(Math.min(maxIndex, carouselIndex + 1));
  };

  // Rendu de l'icône selon le type de package
  const renderIcon = (iconType, className) => {
    switch (iconType) {
      case 'shopping':
        return <ShoppingBagIcon className={className} />;
      case 'building':
        return <OfficeBuildingIcon className={className} />;
      case 'heart':
        return <HeartIcon className={className} />;
      case 'academic':
        return <AcademicCapIcon className={className} />;
      default:
        return <TagIcon className={className} />;
    }
  };

  return (
    <div className="bg-gradient-to-b from-gray-50 to-white py-16 px-4">
      <div className="container mx-auto max-w-7xl">
        {/* Header avec effet d'animation */}
        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <div className="inline-block relative mb-2">
            <motion.div 
              animate={{ 
                scale: [1, 1.2, 1],
                rotate: [0, 5, 0, -5, 0]
              }}
              transition={{ 
                duration: 6,
                repeat: Infinity,
                repeatType: "reverse"
              }}
              className="absolute -top-6 -left-6 w-24 h-24 rounded-full bg-[#FFF4F0] -z-10"
            ></motion.div>
            <h1 className="text-5xl font-bold text-gray-800 relative inline-block">
              Notre Ceinture Digitale
            </h1>
            <motion.div 
              animate={{ 
                scale: [1, 1.1, 1],
                rotate: [0, -3, 0, 3, 0]
              }}
              transition={{ 
                duration: 7,
                repeat: Infinity,
                repeatType: "reverse",
                delay: 0.5
              }}
              className="absolute -bottom-2 -right-8 w-16 h-16 rounded-full bg-[#FFE8DD] -z-10"
            ></motion.div>
          </div>
          <div className="w-24 h-1 bg-[#E84E0E] mx-auto my-4 rounded-full"></div>
          <p className="mt-4 text-gray-600 max-w-2xl mx-auto text-lg">
            Explorez nos réseaux thématiques d'écrans publicitaires stratégiquement positionnés
            dans les sites premium de la ville. Sélectionnez le réseau qui correspond à votre cible.
          </p>
          
          {/* Toggle pour comparer les packages */}
          <div className="mt-8 inline-flex items-center">
            <span className="mr-3 text-gray-600 font-medium">Explorer</span>
            <button 
              onClick={toggleCompareMode}
              className="relative inline-flex h-12 w-24 cursor-pointer rounded-full bg-gray-200 transition-colors duration-300 ease-in-out focus:outline-none"
            >
              <span 
                className={`inline-block h-10 w-10 transform rounded-full bg-white shadow-md transition-transform duration-300 ease-in-out m-1 ${
                  isCompareMode ? 'translate-x-12' : ''
                }`}
              />
              <div className="absolute inset-0 flex items-center justify-between px-3 text-sm font-medium">
                <span className={`${isCompareMode ? 'text-gray-400' : 'text-gray-900'} transition-colors duration-300`}>
                  Voir
                </span>
                <span className={`${isCompareMode ? 'text-gray-900' : 'text-gray-400'} transition-colors duration-300`}>
                  Comparer
                </span>
              </div>
            </button>
          </div>
        </motion.div>
        
        {!isCompareMode ? (
          // Mode normal - Affichage Carousel
          <div className="relative">
            {/* Boutons de navigation */}
            <div className="flex justify-between absolute -left-6 -right-6 top-1/2 transform -translate-y-1/2 px-4 z-10">
              <motion.button 
                whileHover={{ scale: 1.1, backgroundColor: '#FFF4F0' }}
                whileTap={{ scale: 0.95 }}
                onClick={previousSlide}
                disabled={carouselIndex === 0}
                className={`w-12 h-12 rounded-full bg-white shadow-lg flex items-center justify-center transition-all duration-300 ${
                  carouselIndex === 0 ? 'opacity-50 cursor-not-allowed' : 'hover:bg-[#FFF4F0]'
                }`}
              >
                <ChevronLeftIcon className="h-6 w-6 text-gray-700" />
              </motion.button>
              <motion.button 
                whileHover={{ scale: 1.1, backgroundColor: '#FFF4F0' }}
                whileTap={{ scale: 0.95 }}
                onClick={nextSlide}
                disabled={carouselIndex >= maxIndex}
                className={`w-12 h-12 rounded-full bg-white shadow-lg flex items-center justify-center transition-all duration-300 ${
                  carouselIndex >= maxIndex ? 'opacity-50 cursor-not-allowed' : 'hover:bg-[#FFF4F0]'
                }`}
              >
                <ChevronRightIcon className="h-6 w-6 text-gray-700" />
              </motion.button>
            </div>
            
            {/* Carousel des packages */}
            <div className="overflow-hidden">
              <motion.div 
                className="flex transition-all duration-500 ease-out"
                initial={false}
                animate={{ x: -carouselIndex * (100 / itemsPerSlide) + '%' }}
                transition={{ type: "spring", stiffness: 300, damping: 30 }}
              >
                {packageData.map((pkg, index) => (
                  <div 
                    key={pkg.id} 
                    className={`w-full md:w-1/2 xl:w-1/3 flex-shrink-0 px-4`}
                  >
                    <PackageCard 
                      pkg={pkg} 
                      onClick={() => openPackageDetails(pkg)} 
                      index={index}
                      renderIcon={renderIcon}
                    />
                  </div>
                ))}
              </motion.div>
            </div>
            
            {/* Pagination améliorée */}
            <div className="flex justify-center gap-2 mt-12">
              {Array.from({ length: maxIndex + 1 }).map((_, i) => (
                <motion.button 
                  key={i}
                  onClick={() => setCarouselIndex(i)}
                  whileHover={{ scale: 1.2 }}
                  whileTap={{ scale: 0.9 }}
                  className={`h-3 rounded-full transition-all duration-300 ${
                    i === carouselIndex ? 'bg-[#E84E0E] w-10' : 'bg-gray-300 hover:bg-gray-400 w-3'
                  }`}
                  aria-label={`Page ${i + 1}`}
                />
              ))}
            </div>
          </div>
        ) : (
          // Mode comparaison
          <div>
            <div className="mb-8 text-center">
              <p className="text-gray-600">
                Sélectionnez jusqu'à 3 réseaux à comparer
              </p>
              <div className="mt-4 flex flex-wrap gap-2 justify-center">
                {packageData.map(pkg => (
                  <button
                    key={pkg.id}
                    onClick={() => togglePackageSelection(pkg)}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all`}
                    style={{
                      backgroundColor: selectedPackages.some(item => item.id === pkg.id) ? pkg.color : '#f3f4f6',
                      color: selectedPackages.some(item => item.id === pkg.id) ? 'white' : '#374151'
                    }}
                  >
                    {pkg.name}
                    {selectedPackages.some(item => item.id === pkg.id) && (
                      <CheckIcon className="inline-block ml-1 h-4 w-4" />
                    )}
                  </button>
                ))}
              </div>
            </div>
            
            {selectedPackages.length > 0 ? (
              <ComparisonTable packages={selectedPackages} />
            ) : (
              <div className="text-center py-16 bg-gray-50 rounded-xl">
                <div className="w-20 h-20 mx-auto bg-[#FFF4F0] rounded-full flex items-center justify-center mb-4">
                  <motion.div
                    animate={{ 
                      rotate: [0, 15, 0, -15, 0],
                    }}
                    transition={{ 
                      duration: 2,
                      repeat: Infinity,
                    }}
                  >
                    <TagIcon className="h-10 w-10 text-[#E84E0E]" />
                  </motion.div>
                </div>
                <h3 className="text-xl font-medium text-gray-700 mb-2">Sélectionnez des réseaux à comparer</h3>
                <p className="text-gray-500">Cliquez sur les réseaux ci-dessus pour les ajouter à la comparaison</p>
              </div>
            )}
          </div>
        )}
        
        {/* Modal de détails du package */}
        <AnimatePresence>
          {currentPackage && (
            <PackageDetailModal 
              pkg={currentPackage} 
              onClose={closePackageDetails} 
              renderIcon={renderIcon}
            />
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

// Composant pour les cartes de package
const PackageCard = ({ pkg, onClick, index, renderIcon }) => {
  const [isHovered, setIsHovered] = useState(false);
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      onHoverStart={() => setIsHovered(true)}
      onHoverEnd={() => setIsHovered(false)}
      className={`bg-white rounded-3xl overflow-hidden shadow-lg cursor-pointer h-full flex flex-col transition-all duration-500 border-2 relative`}
      style={{ 
        borderColor: pkg.featured ? pkg.color : isHovered ? '#e5e7eb' : 'transparent',
        transform: isHovered ? 'translateY(-8px)' : 'none'
      }}
      onClick={onClick}
    >
      {/* Badge featured */}
      {pkg.featured && (
        <div className="absolute top-0 right-0 z-10">
          <div 
            className="text-white text-xs font-bold px-6 py-1 rounded-bl-xl"
            style={{ backgroundColor: pkg.color }}
          >
            RECOMMANDÉ
          </div>
        </div>
      )}
      
      {/* Image principale */}
      <div className="relative h-58 w-full overflow-hidden">
        <img 
          src={pkg.imageUrl} 
          alt={`${pkg.name} network sites`} 
          className="w-full h-full object-cover transition-transform duration-500"
          style={{ transform: isHovered ? 'scale(1.05)' : 'scale(1)' }}
        />
        <div 
          className="absolute inset-0 bg-gradient-to-b from-transparent to-black opacity-60"
        ></div>
        <div className="absolute bottom-0 left-0 p-4 w-full">
          <h3 className="text-2xl font-bold text-white mb-1">{pkg.name}</h3>
          <p className="text-gray-200 text-sm">{pkg.subtitle}</p>
        </div>
      </div>
      
      {/* Prix - mise en évidence */}
      <div 
        className="relative py-4 px-6 text-center"
        style={{ backgroundColor: pkg.lightColor }}
      >
        <div className="flex items-center justify-center">
          <div 
            className={`absolute -top-6 p-4 rounded-full shadow-lg`}
            style={{ backgroundColor: pkg.color }}
          >
            {renderIcon(pkg.icon, `h-6 w-6 text-white`)}
          </div>
        </div>
        <div className="mt-6">
          <span className="text-3xl font-bold" style={{ color: pkg.color }}>{new Intl.NumberFormat('fr-FR').format(pkg.price)}</span>
          <span className="text-gray-600 ml-1">FCFA</span>
          <p className="text-sm text-gray-600 mt-1">pour {pkg.duration} de diffusion</p>
        </div>
      </div>
      
      {/* Informations clés */}
      <div className="p-6 flex-grow">
        <div className="grid grid-cols-3 gap-2 mb-6">
          <div className="text-center p-2 rounded-lg bg-gray-50">
            <p className="text-lg font-bold text-gray-800">{pkg.siteCount}</p>
            <p className="text-xs text-gray-600">Sites</p>
          </div>
          <div className="text-center p-2 rounded-lg bg-gray-50">
            <p className="text-lg font-bold text-gray-800">{pkg.screenCount}</p>
            <p className="text-xs text-gray-600">Écrans</p>
          </div>
          <div className="text-center p-2 rounded-lg bg-gray-50">
            <p className="text-lg font-bold text-gray-800">{Math.round(pkg.estimatedViews/1000)}K</p>
            <p className="text-xs text-gray-600">Vues</p>
          </div>
        </div>
        
        <div className="mb-6">
          <h4 className="font-medium text-gray-800 mb-2">Types de sites :</h4>
          <div className="flex flex-wrap gap-2 mb-4">
            {pkg.siteTypes.slice(0, 3).map((type, i) => (
              <span 
                key={i}
                className="px-2 py-1 rounded-full text-xs"
                style={{ backgroundColor: pkg.lightColor, color: pkg.color }}
              >
                {type}
              </span>
            ))}
            {pkg.siteTypes.length > 3 && (
              <span className="px-2 py-1 rounded-full text-xs bg-gray-100 text-gray-600">
                +{pkg.siteTypes.length - 3}
              </span>
            )}
          </div>
          
          <p className="text-gray-600 text-sm line-clamp-3">{pkg.description}</p>
        </div>
        
        <h4 className="font-medium text-gray-800 mb-2">Points forts du réseau :</h4>
        <ul className="space-y-2">
          {pkg.benefits.slice(0, 3).map((benefit, i) => (
            <li key={i} className="flex items-start">
              <CheckCircleIcon style={{ color: pkg.color }} className="h-4 w-4 mr-2 flex-shrink-0 mt-0.5" />
              <span className="text-gray-600 text-sm">{benefit}</span>
            </li>
          ))}
          {pkg.benefits.length > 3 && (
            <li className="text-gray-500 text-xs pl-6 italic">
              + {pkg.benefits.length - 3} autres avantages
            </li>
          )}
        </ul>
      </div>
      
      {/* Footer avec bouton */}
      <div className="p-6 border-t border-gray-100">
        <motion.button 
          whileHover={{ scale: 1.03 }}
          whileTap={{ scale: 0.97 }}
          className="w-full py-3 rounded-xl font-medium text-white shadow flex items-center justify-center"
          style={{ backgroundColor: pkg.color }}
        >
          <span>Voir le réseau</span>
          <ArrowRightIcon className="h-5 w-5 ml-2" />
        </motion.button>
      </div>
    </motion.div>
  );
};

// Modal de détail du package
const PackageDetailModal = ({ pkg, onClose, renderIcon }) => {
  const [activeTab, setActiveTab] = useState('overview');
  
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-75 backdrop-blur-sm"
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.9, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.9, y: 20 }}
        transition={{ type: "spring", damping: 25, stiffness: 300 }}
        className="bg-white rounded-3xl shadow-2xl overflow-hidden max-w-6xl w-full max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header avec image de couverture */}
        <div className="relative h-90 w-full overflow-hidden">
          <img 
            src={pkg.imageUrl} 
            alt={`${pkg.name} network`} 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-black/30 to-black/70"></div>
          
          {/* Bouton fermer */}
          <button
            onClick={onClose}
            className="absolute top-6 right-6 p-2 rounded-full bg-white/20 hover:bg-white/40 text-white transition-all"
          >
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          
          {/* Info principale */}
          <div className="absolute bottom-0 left-0 p-8 w-full">
            <div className="flex items-center mb-2">
              <div 
                className="p-3 rounded-xl mr-4"
                style={{ backgroundColor: pkg.color }}
              >
                {renderIcon(pkg.icon, "h-8 w-8 text-white")}
              </div>
              <div>
                <div className="flex items-center">
                  <h2 className="text-3xl font-bold text-white mr-3">{pkg.name}</h2>
                  {pkg.featured && (
                    <span 
                    className="px-3 py-1 rounded-full text-xs font-medium"
                    style={{ backgroundColor: 'rgba(255,255,255,0.2)' }}
                  >
                    RECOMMANDÉ
                  </span>
                )}
              </div>
              <p className="text-gray-200">{pkg.subtitle}</p>
            </div>
          </div>
        </div>
      </div>
      
      {/* Menu onglets */}
      <div className="bg-white px-8 py-4 border-b">
        <div className="flex overflow-x-auto space-x-4">
          <button
            onClick={() => setActiveTab('overview')}
            className={`px-4 py-2 font-medium rounded-lg transition-all ${
              activeTab === 'overview' 
              ? `text-white` 
              : 'text-gray-600 hover:bg-gray-100'
            }`}
            style={{ 
              backgroundColor: activeTab === 'overview' ? pkg.color : 'transparent',
            }}
          >
            Vue d'ensemble
          </button>
          <button
            onClick={() => setActiveTab('sites')}
            className={`px-4 py-2 font-medium rounded-lg transition-all ${
              activeTab === 'sites' 
              ? `text-white` 
              : 'text-gray-600 hover:bg-gray-100'
            }`}
            style={{ 
              backgroundColor: activeTab === 'sites' ? pkg.color : 'transparent',
            }}
          >
            Sites & Emplacements
          </button>
          <button
            onClick={() => setActiveTab('audience')}
            className={`px-4 py-2 font-medium rounded-lg transition-all ${
              activeTab === 'audience' 
              ? `text-white` 
              : 'text-gray-600 hover:bg-gray-100'
            }`}
            style={{ 
              backgroundColor: activeTab === 'audience' ? pkg.color : 'transparent',
            }}
          >
            Audience & Analytics
          </button>
          <button
            onClick={() => setActiveTab('pricing')}
            className={`px-4 py-2 font-medium rounded-lg transition-all ${
              activeTab === 'pricing' 
              ? `text-white` 
              : 'text-gray-600 hover:bg-gray-100'
            }`}
            style={{ 
              backgroundColor: activeTab === 'pricing' ? pkg.color : 'transparent',
            }}
          >
            Tarification & Options
          </button>
        </div>
      </div>
      
      {/* Contenu principal - scrollable */}
      <div className="flex-grow overflow-y-auto p-8">
        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="md:col-span-2">
              <div className="mb-6">
                <h3 className="text-xl font-semibold text-gray-800 mb-4">Description du réseau</h3>
                <p className="text-gray-600 leading-relaxed">{pkg.description}</p>
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-8">
                <div className="bg-gray-50 rounded-xl p-5">
                  <h4 className="text-gray-800 font-medium mb-3">Audience cible</h4>
                  <p className="text-gray-600">{pkg.targetAudience}</p>
                </div>
                <div className="bg-gray-50 rounded-xl p-5">
                  <h4 className="text-gray-800 font-medium mb-3">Idéal pour</h4>
                  <p className="text-gray-600">{pkg.idealFor}</p>
                </div>
              </div>
              
              <div className="mb-8">
                <h3 className="text-xl font-semibold text-gray-800 mb-4">Principaux avantages</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {pkg.benefits.map((benefit, i) => (
                    <div key={i} className="flex items-start">
                      <CheckCircleIcon style={{ color: pkg.color }} className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-600">{benefit}</span>
                    </div>
                  ))}
                </div>
              </div>
              
              <div>
                <h3 className="text-xl font-semibold text-gray-800 mb-4">Secteurs d'activité ciblés</h3>
                <div className="flex flex-wrap gap-2">
                  {pkg.targetingSectors.map((sector, i) => (
                    <span 
                      key={i}
                      className="px-3 py-1.5 rounded-full text-sm"
                      style={{ backgroundColor: pkg.lightColor, color: pkg.color }}
                    >
                      {sector}
                    </span>
                  ))}
                </div>
              </div>
            </div>
            
            <div>
              <div className="sticky top-8">
                <div 
                  className="rounded-2xl overflow-hidden border-2 mb-6"
                  style={{ borderColor: pkg.color }}
                >
                  <div 
                    className="p-6 text-white"
                    style={{ backgroundColor: pkg.color }}
                  >
                    <div className="flex justify-between items-center mb-2">
                      <h3 className="text-xl font-bold">Tarification</h3>
                      <div className="bg-white bg-opacity-20 rounded-full px-3 py-1 text-sm">
                        {pkg.duration}
                      </div>
                    </div>
                    <div className="flex items-baseline mb-4">
                      <span className="text-3xl font-bold">{new Intl.NumberFormat('fr-FR').format(pkg.price)}</span>
                      <span className="ml-1 opacity-90">FCFA</span>
                    </div>
                    <div className="flex items-center space-x-1 text-sm opacity-90">
                      <ClockIcon className="h-4 w-4" />
                      <span>Validité de la campagne : {pkg.duration}</span>
                    </div>
                  </div>
                  
                  <div className="bg-white p-6">
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Nombre de sites</span>
                        <span className="font-medium text-gray-800">{pkg.siteCount}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Nombre d'écrans</span>
                        <span className="font-medium text-gray-800">{pkg.screenCount}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Impressions estimées</span>
                        <span className="font-medium text-gray-800">{new Intl.NumberFormat('fr-FR').format(pkg.estimatedViews)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Coût par 1000 vues</span>
                        <span className="font-medium text-gray-800">
                          {new Intl.NumberFormat('fr-FR').format(Math.round(pkg.price / (pkg.estimatedViews / 1000)))} FCFA
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Analytics</span>
                        <span className="font-medium text-gray-800">{pkg.analytics ? 'Inclus' : 'Non inclus'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Support design</span>
                        <span className="font-medium text-gray-800">{pkg.designSupport}</span>
                      </div>
                    </div>
                    
                    <div className="mt-8 space-y-3">
                      <motion.button 
                        whileHover={{ scale: 1.03 }}
                        whileTap={{ scale: 0.97 }}
                        className="w-full py-3 rounded-xl font-medium text-white shadow flex items-center justify-center"
                        style={{ backgroundColor: pkg.color }}
                      >
                        <ShoppingBagIcon className="h-5 w-5 mr-2" />
                        <span>Réserver ce réseau</span>
                      </motion.button>
                      
                      <button className="w-full py-3 rounded-xl font-medium border-2 border-gray-200 text-gray-700 hover:bg-gray-50 transition-colors">
                        Demander un devis personnalisé
                      </button>
                    </div>
                  </div>
                </div>
                
                <div className="bg-gray-50 rounded-xl p-5">
                  <div className="flex items-center mb-3">
                    <SparklesIcon className="h-5 w-5 text-gray-600 mr-2" />
                    <h4 className="text-gray-800 font-medium">Indice de popularité</h4>
                  </div>
                  <div className="h-2 w-full bg-gray-200 rounded-full mb-2">
                    <div 
                      className="h-2 rounded-full"
                      style={{ 
                        width: `${pkg.popularity}%`,
                        backgroundColor: pkg.color
                      }}
                    ></div>
                  </div>
                  <p className="text-gray-500 text-sm">
                    Ce réseau est choisi par {pkg.popularity}% des annonceurs dans sa catégorie.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {activeTab === 'sites' && (
          <div>
            <div className="mb-8">
              <h3 className="text-xl font-semibold text-gray-800 mb-4">Emplacements & Couverture</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                <div 
                  className="p-5 rounded-xl"
                  style={{ backgroundColor: pkg.lightColor }}
                >
                  <div className="flex items-center mb-3">
                    <div 
                      className="p-2 rounded-full mr-3"
                      style={{ backgroundColor: pkg.color }}
                    >
                      <LocationMarkerIcon className="h-6 w-6 text-white" />
                    </div>
                    <h4 className="font-medium text-gray-800">Zones couvertes</h4>
                  </div>
                  <div className="space-y-2">
                    {pkg.locations.map((location, i) => (
                      <div key={i} className="flex items-center">
                        <div className="w-2 h-2 rounded-full bg-gray-400 mr-2"></div>
                        <span className="text-gray-600">{location}</span>
                      </div>
                    ))}
                  </div>
                </div>
                
                <div 
                  className="p-5 rounded-xl"
                  style={{ backgroundColor: pkg.lightColor }}
                >
                  <div className="flex items-center mb-3">
                    <div 
                      className="p-2 rounded-full mr-3"
                      style={{ backgroundColor: pkg.color }}
                    >
                      <DesktopComputerIcon className="h-6 w-6 text-white" />
                    </div>
                    <h4 className="font-medium text-gray-800">Types d'écrans</h4>
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center">
                      <div className="w-2 h-2 rounded-full bg-gray-400 mr-2"></div>
                      <span className="text-gray-600">Écrans LED grand format (outdoor)</span>
                    </div>
                    <div className="flex items-center">
                      <div className="w-2 h-2 rounded-full bg-gray-400 mr-2"></div>
                      <span className="text-gray-600">Écrans LCD HD (indoor)</span>
                    </div>
                    <div className="flex items-center">
                      <div className="w-2 h-2 rounded-full bg-gray-400 mr-2"></div>
                      <span className="text-gray-600">Totems numériques interactifs</span>
                    </div>
                  </div>
                </div>
                
                <div 
                  className="p-5 rounded-xl"
                  style={{ backgroundColor: pkg.lightColor }}
                >
                  <div className="flex items-center mb-3">
                    <div 
                      className="p-2 rounded-full mr-3"
                      style={{ backgroundColor: pkg.color }}
                    >
                      <EyeIcon className="h-6 w-6 text-white" />
                    </div>
                    <h4 className="font-medium text-gray-800">Audience quotidienne</h4>
                  </div>
                  <div className="flex items-baseline">
                    <span className="text-3xl font-bold text-gray-800">{Math.round(pkg.estimatedViews/pkg.duration.split(' ')[0]).toLocaleString('fr-FR')}</span>
                    <span className="ml-1 text-gray-600">impressions/jour</span>
                  </div>
                  <p className="mt-2 text-gray-600 text-sm">
                    Basé sur notre analyse de fréquentation et notre technologie de comptage
                  </p>
                </div>
              </div>
              
              <div>
                <h4 className="font-medium text-gray-800 mb-3">Exemples de sites dans ce réseau</h4>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {pkg.imageSites.map((image, i) => (
                    <div key={i} className="rounded-xl overflow-hidden">
                      <img src={image} alt={`${pkg.name} site example ${i+1}`} className="w-full h-48 object-cover" />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
        
        {activeTab === 'audience' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <h3 className="text-xl font-semibold text-gray-800 mb-4">Profil de l'audience</h3>
              
              <div className="mb-6 p-5 bg-gray-50 rounded-xl">
                <h4 className="font-medium text-gray-800 mb-3">Audience cible détaillée</h4>
                <p className="text-gray-600 mb-4">{pkg.targetAudience}</p>
                
                <div>
                  <h5 className="text-sm font-medium text-gray-600 mb-2">Répartition démographique estimée</h5>
                  <div className="space-y-3">
                    <div>
                      <div className="flex justify-between text-sm mb-1">
                        <span>Hommes/Femmes</span>
                        <span>55% / 45%</span>
                      </div>
                      <div className="h-2 w-full bg-gray-200 rounded-full">
                        <div 
                          className="h-2 rounded-full"
                          style={{ 
                            width: `55%`,
                            backgroundColor: pkg.color
                          }}
                        ></div>
                      </div>
                    </div>
                    
                    <div>
                      <div className="flex justify-between text-sm mb-1">
                        <span>18-34 ans</span>
                        <span>40%</span>
                      </div>
                      <div className="h-2 w-full bg-gray-200 rounded-full">
                        <div 
                          className="h-2 rounded-full"
                          style={{ 
                            width: `40%`,
                            backgroundColor: pkg.color
                          }}
                        ></div>
                      </div>
                    </div>
                    
                    <div>
                      <div className="flex justify-between text-sm mb-1">
                        <span>35-50 ans</span>
                        <span>45%</span>
                      </div>
                      <div className="h-2 w-full bg-gray-200 rounded-full">
                        <div 
                          className="h-2 rounded-full"
                          style={{ 
                            width: `45%`,
                            backgroundColor: pkg.color
                          }}
                        ></div>
                      </div>
                    </div>
                    
                    <div>
                      <div className="flex justify-between text-sm mb-1">
                        <span>51+ ans</span>
                        <span>15%</span>
                      </div>
                      <div className="h-2 w-full bg-gray-200 rounded-full">
                        <div 
                          className="h-2 rounded-full"
                          style={{ 
                            width: `15%`,
                            backgroundColor: pkg.color
                          }}
                        ></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="p-5 bg-gray-50 rounded-xl">
                <h4 className="font-medium text-gray-800 mb-3">Comportement de l'audience</h4>
                
                <div className="space-y-4">
                  <div>
                    <h5 className="text-sm font-medium text-gray-600 mb-2">Temps d'exposition moyen</h5>
                    <div className="flex items-center">
                      <ClockIcon className="h-5 w-5 text-gray-500 mr-2" />
                      <span className="text-gray-800">15-20 secondes par affichage</span>
                    </div>
                  </div>
                  
                  <div>
                    <h5 className="text-sm font-medium text-gray-600 mb-2">Fréquence de passage</h5>
                    <div className="flex items-center">
                      <ChartBarIcon className="h-5 w-5 text-gray-500 mr-2" />
                      <span className="text-gray-800">3-4 fois par semaine (audience régulière)</span>
                    </div>
                  </div>
                  
                  <div>
                    <h5 className="text-sm font-medium text-gray-600 mb-2">Pics d'affluence</h5>
                    <div className="grid grid-cols-7 gap-1 mt-2">
                      {['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'].map((day, i) => (
                        <div key={i} className="text-center">
                          <div className="text-xs text-gray-500 mb-1">{day}</div>
                          <div 
                            className="h-16 w-full rounded-lg"
                            style={{ 
                              backgroundColor: pkg.lightColor,
                              opacity: i < 5 ? 0.7 + (i * 0.06) : (i === 5 ? 1 : 0.6)
                            }}
                          ></div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div>
              <h3 className="text-xl font-semibold text-gray-800 mb-4">Analytics & Reporting</h3>
              
              <div className="mb-6 p-5 bg-gray-50 rounded-xl">
                <div className="flex items-center mb-4">
                  <div 
                    className="h-10 w-10 rounded-full flex items-center justify-center mr-3"
                    style={{ backgroundColor: pkg.color }}
                  >
                    <ChartBarIcon className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-800">Rapport de performance</h4>
                    <p className="text-gray-500 text-sm">Inclus avec votre campagne</p>
                  </div>
                </div>
                
                <div className="space-y-3">
                  <div className="flex items-start">
                    <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                    <div>
                      <span className="text-gray-700 font-medium">Estimation d'audience</span>
                      <p className="text-gray-500 text-sm">Basée sur notre technologie de comptage</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start">
                    <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                    <div>
                      <span className="text-gray-700 font-medium">Statistiques de diffusion</span>
                      <p className="text-gray-500 text-sm">Nombre de passages, durée totale</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start">
                    <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                    <div>
                      <span className="text-gray-700 font-medium">Répartition par site</span>
                      <p className="text-gray-500 text-sm">Performance détaillée par emplacement</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start">
                    <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                    <div>
                      <span className="text-gray-700 font-medium">Rapport de fin de campagne</span>
                      <p className="text-gray-500 text-sm">Synthèse complète envoyée sous 48h</p>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="p-5 bg-gray-50 rounded-xl">
                <h4 className="font-medium text-gray-800 mb-3">Objectifs marketing atteignables</h4>
                
                <div className="space-y-3">
                  <div className="flex items-center">
                    <div 
                      className="h-8 w-8 rounded-full flex items-center justify-center mr-2"
                      style={{ backgroundColor: pkg.lightColor }}
                    >
                      <EyeIcon className="h-4 w-4" style={{ color: pkg.color }} />
                    </div>
                    <div>
                      <span className="text-gray-700">Visibilité & Notoriété</span>
                    </div>
                    <div className="ml-auto">
                      <div className="flex">
                        {[1, 2, 3, 4, 5].map(star => (
                          <svg 
                            key={star} 
                            className={`h-4 w-4 ${star <= 5 ? 'text-yellow-400' : 'text-gray-300'}`}
                            fill="currentColor" 
                            viewBox="0 0 20 20"
                          >
                            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                          </svg>
                        ))}
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center">
                    <div 
                      className="h-8 w-8 rounded-full flex items-center justify-center mr-2"
                      style={{ backgroundColor: pkg.lightColor }}
                    >
                      <UserGroupIcon className="h-4 w-4" style={{ color: pkg.color }} />
                    </div>
                    <div>
                      <span className="text-gray-700">Ciblage précis</span>
                    </div>
                    <div className="ml-auto">
                      <div className="flex">
                        {[1, 2, 3, 4, 5].map(star => (
                          <svg 
                            key={star} 
                            className={`h-4 w-4 ${star <= 4 ? 'text-yellow-400' : 'text-gray-300'}`}
                            fill="currentColor" 
                            viewBox="0 0 20 20"
                          >
                            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                          </svg>
                        ))}
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center">
                    <div 
                      className="h-8 w-8 rounded-full flex items-center justify-center mr-2"
                      style={{ backgroundColor: pkg.lightColor }}
                    >
                      <LightningBoltIcon className="h-4 w-4" style={{ color: pkg.color }} />
                    </div>
                    <div>
                      <span className="text-gray-700">Impact immédiat</span>
                    </div>
                    <div className="ml-auto">
                      <div className="flex">
                        {[1, 2, 3, 4, 5].map(star => (
                          <svg 
                            key={star} 
                            className={`h-4 w-4 ${star <= 4 ? 'text-yellow-400' : 'text-gray-300'}`}
                            fill="currentColor" 
                            viewBox="0 0 20 20"
                          >
                            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                          </svg>
                        ))}
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center">
                    <div 
                      className="h-8 w-8 rounded-full flex items-center justify-center mr-2"
                      style={{ backgroundColor: pkg.lightColor }}
                      >
                        <CurrencyDollarIcon className="h-4 w-4" style={{ color: pkg.color }} />
                      </div>
                      <div>
                        <span className="text-gray-700">Retour sur investissement</span>
                      </div>
                      <div className="ml-auto">
                        <div className="flex">
                          {[1, 2, 3, 4, 5].map(star => (
                            <svg 
                              key={star} 
                              className={`h-4 w-4 ${star <= 3 ? 'text-yellow-400' : 'text-gray-300'}`}
                              fill="currentColor" 
                              viewBox="0 0 20 20"
                            >
                              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                            </svg>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {activeTab === 'pricing' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-xl font-semibold text-gray-800 mb-4">Formules et tarification</h3>
                
                <div 
                  className="mb-6 p-6 rounded-xl border-2"
                  style={{ borderColor: pkg.color }}
                >
                  <div className="flex items-center mb-4">
                    <div 
                      className="p-3 rounded-xl mr-4"
                      style={{ backgroundColor: pkg.color }}
                    >
                      <TagIcon className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-800">{pkg.name}</h3>
                      <p className="text-gray-600">{pkg.subtitle}</p>
                    </div>
                    <div className="ml-auto">
                      <span className="font-bold text-2xl" style={{ color: pkg.color }}>
                        {new Intl.NumberFormat('fr-FR').format(pkg.price)}
                      </span>
                      <span className="text-gray-600 ml-1">FCFA</span>
                    </div>
                  </div>
                  
                  <div className="mb-4">
                    <div className="flex items-center mb-2">
                      <ClockIcon className="h-5 w-5 text-gray-400 mr-2" />
                      <span className="text-gray-600">Durée de diffusion:</span>
                      <span className="ml-auto font-medium text-gray-800">{pkg.duration}</span>
                    </div>
                    <div className="flex items-center mb-2">
                      <DesktopComputerIcon className="h-5 w-5 text-gray-400 mr-2" />
                      <span className="text-gray-600">Nombre d'écrans:</span>
                      <span className="ml-auto font-medium text-gray-800">{pkg.screenCount} écrans</span>
                    </div>
                    <div className="flex items-center mb-2">
                      <LocationMarkerIcon className="h-5 w-5 text-gray-400 mr-2" />
                      <span className="text-gray-600">Nombre de sites:</span>
                      <span className="ml-auto font-medium text-gray-800">{pkg.siteCount} sites</span>
                    </div>
                    <div className="flex items-center">
                      <EyeIcon className="h-5 w-5 text-gray-400 mr-2" />
                      <span className="text-gray-600">Impressions estimées:</span>
                      <span className="ml-auto font-medium text-gray-800">
                        {new Intl.NumberFormat('fr-FR').format(pkg.estimatedViews)}
                      </span>
                    </div>
                  </div>
                  
                  <motion.button 
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="w-full py-3 rounded-xl font-medium text-white shadow flex items-center justify-center"
                    style={{ backgroundColor: pkg.color }}
                  >
                    <span>Réserver maintenant</span>
                    <ArrowRightIcon className="h-5 w-5 ml-2" />
                  </motion.button>
                </div>
                
                <div className="mb-6 p-6 rounded-xl bg-gray-50">
                  <h4 className="font-medium text-gray-800 mb-4">Options complémentaires</h4>
                  
                  <div className="space-y-4">
                    <div className="flex items-start">
                      <input 
                        type="checkbox" 
                        id="option1" 
                        className="h-5 w-5 rounded text-blue-600 mt-0.5 mr-3" 
                      />
                      <div>
                        <label htmlFor="option1" className="font-medium text-gray-700 block">
                          Extension de durée (+7 jours)
                        </label>
                        <p className="text-gray-500 text-sm">
                          Prolongez votre campagne pour une meilleure exposition
                        </p>
                        <p className="text-gray-800 font-medium mt-1">
                          +{Math.round(pkg.price * 0.8).toLocaleString('fr-FR')} FCFA
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-start">
                      <input 
                        type="checkbox" 
                        id="option2" 
                        className="h-5 w-5 rounded text-blue-600 mt-0.5 mr-3"
                      />
                      <div>
                        <label htmlFor="option2" className="font-medium text-gray-700 block">
                          Création graphique premium
                        </label>
                        <p className="text-gray-500 text-sm">
                          Conception de votre visuel par notre équipe de designers
                        </p>
                        <p className="text-gray-800 font-medium mt-1">
                          +150 000 FCFA
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-start">
                      <input 
                        type="checkbox" 
                        id="option3" 
                        className="h-5 w-5 rounded text-blue-600 mt-0.5 mr-3"
                      />
                      <div>
                        <label htmlFor="option3" className="font-medium text-gray-700 block">
                          Analytics avancés
                        </label>
                        <p className="text-gray-500 text-sm">
                          Rapports détaillés en temps réel et analyses démographiques
                        </p>
                        <p className="text-gray-800 font-medium mt-1">
                          +80 000 FCFA
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-start">
                      <input 
                        type="checkbox" 
                        id="option4" 
                        className="h-5 w-5 rounded text-blue-600 mt-0.5 mr-3"
                      />
                      <div>
                        <label htmlFor="option4" className="font-medium text-gray-700 block">
                          Exclusivité sectorielle
                        </label>
                        <p className="text-gray-500 text-sm">
                          Garantie qu'aucun concurrent de votre secteur n'apparaîtra sur ces écrans
                        </p>
                        <p className="text-gray-800 font-medium mt-1">
                          +200 000 FCFA
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              <div>
                <h3 className="text-xl font-semibold text-gray-800 mb-4">Autres formules disponibles</h3>
                
                <div className="space-y-4">
                  {packageData.filter(p => p.id !== pkg.id).map((p, i) => (
                    <div 
                      key={p.id}
                      className="p-4 border rounded-xl hover:shadow-md transition-all duration-300 cursor-pointer"
                    >
                      <div className="flex items-center">
                        <div 
                          className="p-2 rounded-lg mr-3"
                          style={{ backgroundColor: p.lightColor }}
                        >
                          {renderIcon(p.icon, `h-5 w-5`)}
                        </div>
                        <div>
                          <h4 className="font-medium text-gray-800">{p.name}</h4>
                          <p className="text-gray-500 text-sm">{p.subtitle}</p>
                        </div>
                        <div className="ml-auto">
                          <span className="font-bold" style={{ color: p.color }}>
                            {new Intl.NumberFormat('fr-FR').format(p.price)}
                          </span>
                          <span className="text-gray-600 text-sm ml-1">FCFA</span>
                        </div>
                      </div>
                      
                      <div className="mt-3 grid grid-cols-3 gap-2 text-center text-xs">
                        <div className="py-1 px-2 rounded bg-gray-50 text-gray-600">
                          {p.siteCount} sites
                        </div>
                        <div className="py-1 px-2 rounded bg-gray-50 text-gray-600">
                          {p.screenCount} écrans
                        </div>
                        <div className="py-1 px-2 rounded bg-gray-50 text-gray-600">
                          {p.duration}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                
                <div className="mt-8 bg-gray-50 rounded-xl p-6">
                  <h4 className="font-medium text-gray-800 mb-3">Besoin d'une solution sur mesure ?</h4>
                  <p className="text-gray-600 mb-4">
                    Nous pouvons créer un réseau d'affichage personnalisé selon vos besoins spécifiques et votre budget.
                  </p>
                  <div className="flex items-center justify-between">
                    <button className="px-4 py-2 rounded-lg border border-gray-300 hover:bg-gray-100 text-gray-700 font-medium transition-colors">
                      Nous contacter
                    </button>
                    <button className="px-4 py-2 rounded-lg bg-[#E84E0E] hover:bg-[#D04309] text-white font-medium transition-colors flex items-center">
                      <PlusCircleIcon className="h-5 w-5 mr-1" />
                      Demander un devis
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
};

// Tableau comparatif des packages
const ComparisonTable = ({ packages }) => {
  return (
    <div className="bg-white rounded-xl shadow-lg overflow-hidden">
      <div className="grid" style={{ gridTemplateColumns: `250px repeat(${packages.length}, 1fr)` }}>
        {/* En-tête des colonnes */}
        <div className="bg-gray-100 p-6 border-b border-r"></div>
        {packages.map((pkg) => (
          <div 
            key={pkg.id} 
            className="p-6 border-b text-center relative"
            style={{ backgroundColor: pkg.lightColor }}
          >
            {pkg.featured && (
              <div 
                className="absolute top-0 left-0 right-0 text-white text-xs font-bold py-1"
                style={{ backgroundColor: pkg.color }}
              >
                RECOMMANDÉ
              </div>
            )}
            <div 
              className={`rounded-full h-14 w-14 mx-auto mb-3 flex items-center justify-center ${pkg.featured ? 'mt-5' : 'mt-0'}`}
              style={{ backgroundColor: pkg.color }}
            >
              {pkg.icon === 'shopping' && <ShoppingBagIcon className="h-8 w-8 text-white" />}
              {pkg.icon === 'building' && <OfficeBuildingIcon className="h-8 w-8 text-white" />}
              {pkg.icon === 'heart' && <HeartIcon className="h-8 w-8 text-white" />}
              {pkg.icon === 'academic' && <AcademicCapIcon className="h-8 w-8 text-white" />}
            </div>
            <h3 className="text-xl font-bold mb-1" style={{ color: pkg.color }}>{pkg.name}</h3>
            <p className="text-gray-600 text-sm mb-4">{pkg.subtitle}</p>
            <div className="flex justify-center items-baseline mb-3">
              <span className="text-2xl font-bold text-gray-800">
                {new Intl.NumberFormat('fr-FR').format(pkg.price)}
              </span>
              <span className="text-gray-600 ml-1">FCFA</span>
            </div>
            <p className="text-gray-500 text-sm">{pkg.duration}</p>
          </div>
        ))}
        
        {/* Détails principales */}
        <div className="p-6 border-b border-r bg-gray-50 font-medium text-gray-700">
          Specifications principales
        </div>
        {packages.map((pkg) => (
          <div key={`${pkg.id}-main`} className="p-6 border-b text-center grid items-center">
            <div className="space-y-4 py-2">
              <div>
                <div className="text-sm text-gray-500">Nombre de sites</div>
                <div className="font-bold text-gray-800">{pkg.siteCount}</div>
              </div>
              <div>
                <div className="text-sm text-gray-500">Nombre d'écrans</div>
                <div className="font-bold text-gray-800">{pkg.screenCount}</div>
              </div>
              <div>
                <div className="text-sm text-gray-500">Impressions estimées</div>
                <div className="font-bold text-gray-800">
                  {new Intl.NumberFormat('fr-FR').format(pkg.estimatedViews)}
                </div>
              </div>
            </div>
          </div>
        ))}
        
        {/* Types d'écrans */}
        <div className="p-6 border-b border-r bg-gray-50 font-medium text-gray-700">
          Types d'écrans
        </div>
        {packages.map((pkg) => (
          <div key={`${pkg.id}-screens`} className="p-6 border-b grid items-center">
            <div className="space-y-1">
              {pkg.siteTypes.map((type, i) => (
                <div key={i} className="flex items-center">
                  <CheckIcon className="h-5 w-5 mr-2" style={{ color: pkg.color }} />
                  <span className="text-gray-600 text-sm">{type}</span>
                </div>
              ))}
            </div>
          </div>
        ))}
        
        {/* Zones couvertes */}
        <div className="p-6 border-b border-r bg-gray-50 font-medium text-gray-700">
          Zones couvertes
        </div>
        {packages.map((pkg) => (
          <div key={`${pkg.id}-locations`} className="p-6 border-b">
            <div className="flex flex-wrap gap-2 justify-center">
              {pkg.locations.map((location, i) => (
                <span 
                  key={i}
                  className="px-2 py-1 rounded-full text-xs"
                  style={{ backgroundColor: pkg.lightColor, color: pkg.color }}
                >
                  {location}
                </span>
              ))}
            </div>
          </div>
        ))}
        
        {/* Audience cible */}
        <div className="p-6 border-b border-r bg-gray-50 font-medium text-gray-700">
          Audience cible
        </div>
        {packages.map((pkg) => (
          <div key={`${pkg.id}-audience`} className="p-6 border-b text-center">
            <p className="text-gray-600 text-sm">{pkg.targetAudience}</p>
          </div>
        ))}
        
        {/* Avantages clés */}
        <div className="p-6 border-b border-r bg-gray-50 font-medium text-gray-700">
          Avantages clés
        </div>
        {packages.map((pkg) => (
          <div key={`${pkg.id}-benefits`} className="p-6 border-b">
            <div className="space-y-2">
              {pkg.benefits.slice(0, 3).map((benefit, i) => (
                <div key={i} className="flex items-start">
                  <CheckCircleIcon style={{ color: pkg.color }} className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-600 text-sm">{benefit}</span>
                </div>
              ))}
              {pkg.benefits.length > 3 && (
                <p className="text-xs text-gray-500 italic pl-7">+ {pkg.benefits.length - 3} autres avantages</p>
              )}
            </div>
          </div>
        ))}
        
        {/* Indice de popularité */}
        <div className="p-6 border-b border-r bg-gray-50 font-medium text-gray-700">
          Indice de popularité
        </div>
        {packages.map((pkg) => (
          <div key={`${pkg.id}-popularity`} className="p-6 border-b grid items-center">
            <div>
              <div className="h-2 w-full bg-gray-200 rounded-full mb-2">
                <div 
                  className="h-2 rounded-full"
                  style={{ 
                    width: `${pkg.popularity}%`,
                    backgroundColor: pkg.color
                  }}
                ></div>
              </div>
              <p className="text-gray-600 text-sm text-center">
                {pkg.popularity}% de satisfaction
              </p>
            </div>
          </div>
        ))}
        
        {/* Boutons d'action */}
        <div className="p-6 border-r bg-gray-100 font-medium text-gray-700"></div>
        {packages.map((pkg) => (
          <div key={`${pkg.id}-action`} className="p-6 text-center">
            <motion.button 
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
              className="w-full py-3 rounded-xl font-medium text-white shadow mb-3"
              style={{ backgroundColor: pkg.color }}
            >
              <span>Réserver</span>
            </motion.button>
            <button className="text-gray-600 text-sm hover:underline">
              Plus de détails
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

export default PackagesDisplay;